import clsx from 'clsx'
import { router } from 'expo-router'
import { type PropsWithChildren, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { CommonHeader } from '@/src/elements/templates/CommonLayout'
import { Typography } from '@/src/elements/text/Typography'
import { WatchmanThumbnail } from '@/src/elements/watchmanThumbnail/WatchmanThumbnail'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { AccessibilityHiddenTitle } from '@/src/features/accessibility/ui/components/AccessibilityHiddenTitle'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { useIntentionQuery } from '@/src/features/intention/services/useIntentionQuery'
import { useIntentionsByTypeQuery } from '@/src/features/intention/services/useIntentionsByTypeQuery'
import { isSameDayAndMonth } from '@/src/general/primitives/dates/dayjs'
import { useRoutes } from '@/src/general/utils/routes'

import { HappyBirthdayCard } from '../../../components/HappyBirthdayCard/HappyBirthdayCard'
import { PapaTitle } from '../../../components/PapaStyle/PapaTitle'
import { usePapaStyle } from '../../../components/PapaStyle/usePapaStyle'

// FIXME : Would be better to get birthday intention related to the user inside the user object returned by /me
export const PrayerSpaceHeader = ({ isV2 = false, children }: PropsWithChildren<{ isV2?: boolean }>) => {
  const { routes } = useRoutes()
  const me = useMeQuery()
  const { showPapaStyle } = usePapaStyle()

  const myBirthdayDate = useMemo(() => (me.data?.birthdate ? new Date(me.data?.birthdate) : null), [me.data?.birthdate])
  const now = useMemo(() => new Date(), [])

  const isMyBirthday = useMemo(
    () => (myBirthdayDate ? isSameDayAndMonth(myBirthdayDate, now) : false),
    [myBirthdayDate, now],
  )

  // Birthday intentions are only displayed on the user's birthday.
  const { intentions } = useIntentionsByTypeQuery(isMyBirthday, INTENTION_TYPE.BIRTHDAY)
  const intention = intentions?.find((i) => i.user?.id === me.data?.id)
  const { data } = useIntentionQuery(isMyBirthday ? intention?.id : undefined, undefined, INTENTION_TYPE.BIRTHDAY)

  const { initializeRef } = useAccessibilityContext()
  return (
    <View>
      <View className="web:flex-1">
        <CommonHeader />
      </View>
      <AccessibilityHiddenTitle
        ref={(ref) => {
          initializeRef(ref, 'main')
        }}
      >
        <FormattedMessage id="tab.prayer-space" />
      </AccessibilityHiddenTitle>
      <View className={clsx('max-w-5xl w-full items-start mx-auto mt-10', { 'max-w-screen-md': !isV2 })}>
        {me?.data?.name && !!isV2 && (
          <Typography
            heading="h2"
            className={clsx('text-left px-4 pb-2 font-black', showPapaStyle && 'text-leonxivfr')}
            size="xl"
            bold
            contrast="high"
          >
            <FormattedMessage id="words.hello.user" values={{ userName: me.data?.name }} />
          </Typography>
        )}
        <PapaTitle isV2={isV2} />
        {children}
        {isMyBirthday && (
          <View className="w-full px-2 md:px-5">
            <HappyBirthdayCard
              className="min-w-full"
              prayers={data?.prayers || []}
              username={me.data?.name || ''}
              onChipPress={() => {
                return (
                  me.data?.intention &&
                  router.navigate(routes.intentionByIdPrayers(me.data?.intention.id, INTENTION_TYPE.BIRTHDAY))
                )
              }}
            />
          </View>
        )}
        {me.data?.isPrayerWatchman && (
          <View className="my-4 w-full px-2 md:px-5">
            <WatchmanThumbnail />
          </View>
        )}
      </View>
    </View>
  )
}
